import PropTypes from "prop-types"
import { useEffect, useRef } from "react"
import { connect } from "react-redux"

import withTotalItemPrice from "areas/product/hoc/with-total-item-price"
import { addToCart, setFixedATC } from "areas/product/store/actions"
import Button from "components/ui/button"
import Image from "components/ui/image"
import { jewlr_t } from "helpers/application"
import { getImgSrcSet } from "helpers/image"
import { to_currency } from "helpers/number"
import useWindowDimensions from "helpers/window-dimensions"
import { cn } from "styles/utils"

const InternalAddToCartButton = ({
  addToCart,
  fixedATC,
  header,
  imageAreaRef,
  isAddingToCart,
  lid,
  mobileStickyAtcExp,
  onThumbnailClick,
  prefStore,
  previewImage,
  setFixedATC,
  totalItemPrice,
  wizardLoading,
  // wizardRef,
}) => {
  const { isMobile } = useWindowDimensions()
  const inPageATCRef = useRef()
  const cancelled = useRef(false)

  const cartStr = jewlr_t("Cart")
  const showThumbnail = isMobile && mobileStickyAtcExp === 1 && previewImage

  useEffect(() => {
    if (!isMobile) {
      setFixedATC(false)
      return
    }

    const imageArea = imageAreaRef.current
    const inPageATC = inPageATCRef.current

    if (!imageArea || !inPageATC) return

    let observedEl = mobileStickyAtcExp === 1 ? imageArea : inPageATC
    const observer = new IntersectionObserver(([entry]) => {
      const elInView =
        mobileStickyAtcExp === 1
          ? entry.isIntersecting
          : entry.boundingClientRect.bottom > 0
      setFixedATC(!elInView)
    })

    observer.observe(observedEl)

    return () => observer.disconnect()
  }, [isMobile, mobileStickyAtcExp])

  // useLayoutEffect(() => {
  //   const addToCartScrollTick = () => {
  //     if (!cancelled.current) {
  //       if (isMobile) {
  //         window.requestAnimationFrame(() => {
  //           if (wizardRef.current) {
  //             const rect = wizardRef.current.getBoundingClientRect()
  //             if (!rect) return

  //             setFixedATC(rect.bottom <= 0)
  //           }
  //         })
  //       } else {
  //         setFixedATC(false)
  //       }
  //     }
  //   }

  //   addToCartScrollTick()
  //   window.addEventListener("scroll", addToCartScrollTick, false)
  //   return () =>
  //     window.removeEventListener("scroll", addToCartScrollTick, false)
  // }, [isMobile, wizardRef.current])

  useEffect(() => {
    return () => (cancelled.current = true)
  }, [])

  return (
    <>
      {fixedATC && (
        <div
          className={cn(
            "z-2 tablet:shadow-none tablet:static tablet:w-auto tablet:p-0 shadow-(--box-shadow) fixed bottom-0 left-0 w-full bg-white",
            showThumbnail ? "flex items-center gap-12 px-16 py-10" : "p-16"
          )}
          style={{ "--box-shadow": "0 -4px 4px 0 rgba(0, 0, 0, 0.15)" }}
        >
          {showThumbnail && (
            <Button
              className="border-grey-10 relative h-60 w-80 flex-shrink-0 overflow-hidden border"
              onClick={() => onThumbnailClick()}
            >
              <Image
                alt="Product preview"
                className="h-full w-full object-contain"
                height="60"
                sizes="80px"
                src={previewImage.src}
                srcSet={
                  previewImage.raw_src
                    ? getImgSrcSet(previewImage.raw_src, 240, {
                        dim_key: "dim",
                        sizes: [80, 160],
                      })
                    : previewImage.srcSet
                }
                width="80"
              />
              <span className="text-10 absolute bottom-0 left-0 whitespace-nowrap px-4 py-2 text-center text-black">
                Click to enlarge
              </span>
            </Button>
          )}
          <Button
            className="js-add-cart text-18 w-full flex-1"
            disabled={!prefStore || isAddingToCart || wizardLoading}
            loading={isAddingToCart}
            onClick={(e) => addToCart(e, isAddingToCart)}
            rel="nofollow"
            type="submit"
            variant="secondary"
          >
            {to_currency(totalItemPrice)} &ndash;{" "}
            {header.params_olid_present
              ? "Update Order"
              : lid
                ? isAddingToCart
                  ? `Updating ${cartStr}...`
                  : `Update ${cartStr}`
                : isAddingToCart
                  ? `Adding to ${cartStr}...`
                  : `Add to ${cartStr}`}
          </Button>
        </div>
      )}
      <Button
        className="js-add-cart text-18 mb-20 w-full flex-1"
        data-cy="product-wizard-add-to-cart-btn"
        disabled={!prefStore || isAddingToCart || wizardLoading}
        loading={isAddingToCart}
        onClick={(e) => addToCart(e, isAddingToCart)}
        ref={inPageATCRef}
        rel="nofollow"
        type="submit"
        variant="secondary"
      >
        {to_currency(totalItemPrice)} &ndash;{" "}
        {header.params_olid_present
          ? "Update Order"
          : lid
            ? isAddingToCart
              ? `Updating ${cartStr}...`
              : `Update ${cartStr}`
            : isAddingToCart
              ? `Adding to ${cartStr}...`
              : `Add to ${cartStr}`}
      </Button>
    </>
  )
}

InternalAddToCartButton.propTypes = {
  addToCart: PropTypes.func,
  fixedATC: PropTypes.bool,
  header: PropTypes.object,
  imageAreaRef: PropTypes.object,
  isAddingToCart: PropTypes.bool,
  lid: PropTypes.string,
  mobileStickyAtcExp: PropTypes.number,
  onThumbnailClick: PropTypes.func,
  prefStore: PropTypes.bool,
  previewImage: PropTypes.object,
  setFixedATC: PropTypes.func,
  totalItemPrice: PropTypes.number,
  wizardLoading: PropTypes.string,
  // wizardRef: PropTypes.object,
}

const mapStateToProps = (state) => {
  return {
    fixedATC: state.wizardUI.fixedATC,
    header: state.wizard.header,
    isAddingToCart: state.wizardUI.isAddingToCart,
    lid: state.wizardUI.lid,
    mobileStickyAtcExp: state.experiment.mobileStickyAtc.variant_id,
    prefStore: state.wizardUI.prefStore,
    wizardLoading: state.wizard.loading,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    addToCart: (e, isAddingToCart) => {
      e.preventDefault()
      if (!isAddingToCart) {
        dispatch(addToCart())
      }
    },
    setFixedATC: (fixed) => dispatch(setFixedATC(fixed)),
  }
}

const AddToCartButton = connect(
  mapStateToProps,
  mapDispatchToProps
)(InternalAddToCartButton)

export default withTotalItemPrice(AddToCartButton)
